/**
 * Godown Balances (Feb 2026)
 * Live per-godown, per-material balance sheet aggregated from every
 * stock_movement doc. Tabular view with running IN / OUT / Balance.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { WarehouseIcon, ArrowsClockwiseIcon, TrashIcon, CaretDownIcon, CaretRightIcon, StackIcon } from "@phosphor-icons/react";
import KindBadge from "@/components/KindBadge";

const fmt = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 });

export default function GodownBalances() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [godown, setGodown] = useState("all");
  const [q, setQ] = useState("");
  // Feb 2026 · Godown-name → kind map (RM/WIP/FG) so every row gets a
  // colour-coded category badge and staff know at a glance which pool
  // the movement belongs to.
  const [godownMeta, setGodownMeta] = useState({});
  // Feb 25 · Batch-Wise Godown Stock · lazy-loaded per (godown_id|rm_code).
  const [openBatches, setOpenBatches] = useState({});
  const [batchesData, setBatchesData] = useState({});
  const [batchesLoading, setBatchesLoading] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, { data: gs }] = await Promise.all([
        api.get("/godown-balances"),
        api.get("/godowns/").catch(() => ({ data: [] })),
      ]);
      const rowList = data || [];
      setRows(rowList);
      const m = {};
      for (const g of (gs || [])) m[g.name] = g.kind || "";
      setGodownMeta(m);
      // Feb 25 · Auto-expand batches for every WIP row so plant staff
      // see batch-wise stock inline the moment the page loads (no click
      // needed). RM/FG rows stay collapsed by default to keep the page
      // fast — click the chevron to drill in.
      const wipKeys = rowList
        .filter(r => (m[r.godown_name] || "").toUpperCase() === "WIP")
        .map(r => `${r.godown_id}|${r.rm_code}`);
      if (wipKeys.length) {
        const nextOpen = {};
        for (const k of wipKeys) nextOpen[k] = true;
        setOpenBatches(nextOpen);
        // Fetch all in parallel so the drill-down populates fast.
        const rowByKey = Object.fromEntries(rowList.map(r => [`${r.godown_id}|${r.rm_code}`, r]));
        const loadingMap = {};
        for (const k of wipKeys) loadingMap[k] = true;
        setBatchesLoading(loadingMap);
        const results = await Promise.allSettled(wipKeys.map(k => {
          const r = rowByKey[k];
          return api.get("/godown-balances/batches", {
            params: { godown_id: r.godown_id, rm_code: r.rm_code },
          });
        }));
        const dataMap = {};
        results.forEach((res, i) => {
          const k = wipKeys[i];
          dataMap[k] = res.status === "fulfilled" ? (res.value.data || []) : [];
        });
        setBatchesData(dataMap);
        setBatchesLoading({});
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Feb 2026 · "Delete" here wipes every underlying stock_movement doc
  // for the {godown_id, rm_code} pair — the aggregated row instantly
  // disappears from the table as a result.
  const remove = async (r) => {
    if (!window.confirm(`Delete every stock movement for ${r.rm_code} in ${r.godown_name}? Balance history for this line will be lost.`)) return;
    try {
      const { data } = await api.delete(`/godown-balances/clear?godown_id=${encodeURIComponent(r.godown_id || "")}&rm_code=${encodeURIComponent(r.rm_code || "")}`);
      toast.success(`${data.deleted} movement${data.deleted === 1 ? "" : "s"} removed`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const godowns = useMemo(() => Array.from(new Set(rows.map(r => r.godown_name).filter(Boolean))).sort(), [rows]);
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter(r =>
      (godown === "all" || r.godown_name === godown) &&
      (!needle || (r.rm_code || "").toLowerCase().includes(needle))
    );
  }, [rows, godown, q]);

  // Feb 25 · Toggle the batch drill-down for a (godown_id, rm_code) row.
  // First open triggers a fetch of /godown-balances/batches; subsequent
  // toggles just flip visibility (cached client-side per row).
  const toggleBatches = async (r) => {
    const key = `${r.godown_id}|${r.rm_code}`;
    const nextOpen = !openBatches[key];
    setOpenBatches(m => ({ ...m, [key]: nextOpen }));
    if (nextOpen && !batchesData[key]) {
      setBatchesLoading(m => ({ ...m, [key]: true }));
      try {
        const { data } = await api.get("/godown-balances/batches", {
          params: { godown_id: r.godown_id, rm_code: r.rm_code },
        });
        setBatchesData(m => ({ ...m, [key]: data || [] }));
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load batches");
        setBatchesData(m => ({ ...m, [key]: [] }));
      } finally {
        setBatchesLoading(m => ({ ...m, [key]: false }));
      }
    }
  };

  const byGodown = useMemo(() => {
    const m = {};
    for (const r of rows) {
      const k = r.godown_name || "—";
      if (!m[k]) m[k] = { in: 0, out: 0, bal: 0, lines: 0 };
      m[k].in += Number(r.in_qty) || 0;
      m[k].out += Number(r.out_qty) || 0;
      m[k].bal += Number(r.balance) || 0;
      m[k].lines += 1;
    }
    return m;
  }, [rows]);

  return (
    <div className="p-6 space-y-5" data-testid="godown-balances-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline mb-1 flex items-center gap-2"><WarehouseIcon size={11} weight="bold" />Inventory · Balances</div>
          <h1 className="font-display text-3xl font-black tracking-tight text-white">Godown Balances</h1>
          <div className="text-xs text-zinc-500 mt-1">Live IN / OUT / Balance per godown &amp; material · aggregated from every stock movement</div>
        </div>
        <button onClick={load} disabled={loading} className="h-10 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-white text-sm rounded-sm flex items-center gap-2">
          <ArrowsClockwiseIcon size={14} weight="bold" />Refresh
        </button>
      </div>

      <div className={`grid gap-3 grid-cols-2 md:grid-cols-${Math.min(godowns.length || 1, 5)}`}>
        {godowns.map(g => (
          <div key={g} className="border border-[var(--border)] rounded-sm p-3 bg-[var(--bg-2)]/30">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 truncate" title={g}>{g}</div>
            <div className="text-2xl font-black text-white font-display mt-1">{fmt(byGodown[g].bal)}</div>
            <div className="text-[10px] text-zinc-500 mt-1 font-mono">
              <span className="text-emerald-400">IN {fmt(byGodown[g].in)}</span>
              <span className="mx-2">·</span>
              <span className="text-red-400">OUT {fmt(byGodown[g].out)}</span>
              <span className="mx-2">·</span>
              {byGodown[g].lines} line{byGodown[g].lines > 1 ? "s" : ""}
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3 flex-wrap text-[11px]">
        <span className="text-zinc-500 font-mono uppercase tracking-widest">Godown:</span>
        {["all", ...godowns].map(g => (
          <button key={g} onClick={() => setGodown(g)}
            className={`px-2.5 py-1 rounded-sm border font-mono uppercase tracking-widest ${godown === g ? "border-[var(--accent)] bg-[var(--accent)]/10 text-white" : "border-[var(--border)] text-zinc-500 hover:text-white"}`}>{g === "all" ? "All" : g}</button>
        ))}
        <button
          data-testid="bal-expand-all"
          title="Show batch breakdown for every row"
          onClick={async () => {
            const keys = filtered.map(r => `${r.godown_id}|${r.rm_code}`);
            const missing = keys.filter(k => !batchesData[k]);
            const nextOpen = { ...openBatches };
            for (const k of keys) nextOpen[k] = true;
            setOpenBatches(nextOpen);
            if (missing.length) {
              const rowByKey = Object.fromEntries(filtered.map(r => [`${r.godown_id}|${r.rm_code}`, r]));
              const loadingMap = { ...batchesLoading };
              for (const k of missing) loadingMap[k] = true;
              setBatchesLoading(loadingMap);
              const results = await Promise.allSettled(missing.map(k => {
                const r = rowByKey[k];
                return api.get("/godown-balances/batches", { params: { godown_id: r.godown_id, rm_code: r.rm_code } });
              }));
              const dataMap = { ...batchesData };
              const doneLoading = { ...loadingMap };
              results.forEach((res, i) => {
                const k = missing[i];
                dataMap[k] = res.status === "fulfilled" ? (res.value.data || []) : [];
                doneLoading[k] = false;
              });
              setBatchesData(dataMap);
              setBatchesLoading(doneLoading);
            }
          }}
          className="px-2.5 py-1 rounded-sm border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 font-mono uppercase tracking-widest">
          Expand All
        </button>
        <button
          data-testid="bal-collapse-all"
          onClick={() => setOpenBatches({})}
          className="px-2.5 py-1 rounded-sm border border-[var(--border)] text-zinc-400 hover:text-white font-mono uppercase tracking-widest">
          Collapse All
        </button>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search material…"
          className="ml-auto min-w-[220px] h-8 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)]" />
      </div>

      <div className="tactical-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="balances-table">
          <thead>
            <tr className="text-xs uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]">
              <th className="w-8"></th>
              <th className="text-left px-4 py-3">Godown</th>
              <th className="text-left px-4 py-3">Type</th>
              <th className="text-left px-4 py-3">Material</th>
              <th className="text-right px-4 py-3" title="Incoming stock from another floor / RM Store (STAGE_TRANSFER_IN, MRQ_ISSUE)">Received</th>
              <th className="text-right px-4 py-3" title="RM consumed by production entries at this floor">Consumed</th>
              <th className="text-right px-4 py-3" title="Output created by production entries at this floor">Produced</th>
              <th className="text-right px-4 py-3" title="Transferred out to the next stage (STAGE_TRANSFER_OUT)">Issued</th>
              <th className="text-right px-4 py-3" title="(Received + Produced) − (Consumed + Issued)">Balance</th>
              <th className="text-left px-4 py-3">UoM</th>
              <th className="text-right px-4 py-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r, i) => {
              const key = `${r.godown_id}|${r.rm_code}`;
              const isOpen = !!openBatches[key];
              const batches = batchesData[key];
              const bLoading = !!batchesLoading[key];
              return (
                <>
                <tr key={key} className="border-b border-[var(--border)] hover:bg-[var(--bg-2)]/40" data-testid={`bal-row-${i}`}>
                  <td className="px-2 py-3 text-center">
                    <button
                      onClick={() => toggleBatches(r)}
                      title={isOpen ? "Hide batches" : "Show batches at this stage"}
                      data-testid={`bal-toggle-${i}`}
                      className="p-1 rounded-sm text-zinc-500 hover:text-white hover:bg-[var(--bg-2)]"
                    >
                      {isOpen ? <CaretDownIcon size={11} weight="bold" /> : <CaretRightIcon size={11} weight="bold" />}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-zinc-300">{r.godown_name}</td>
                  <td className="px-4 py-3"><KindBadge kind={godownMeta[r.godown_name]} /></td>
                  <td className="px-4 py-3 font-mono text-[var(--accent)]">{r.rm_code}</td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums text-emerald-300" data-testid={`bal-received-${i}`}>{fmt(r.received)}</td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums text-amber-300" data-testid={`bal-consumed-${i}`}>{fmt(r.consumed)}</td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums text-sky-300" data-testid={`bal-produced-${i}`}>{fmt(r.produced)}</td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums text-red-300" data-testid={`bal-issued-${i}`}>{fmt(r.issued)}</td>
                  <td className={`px-4 py-3 text-right font-mono tabular-nums ${r.balance < 0 ? "text-red-400" : r.balance === 0 ? "text-zinc-500" : "text-white"}`}>{fmt(r.balance)}</td>
                  <td className="px-4 py-3 text-zinc-400">{r.uom}</td>
                  <td className="px-4 py-3 text-right">
                    <button title="Delete movements" data-testid={`bal-del-${i}`} onClick={() => remove(r)}
                      className="p-1.5 rounded-sm text-zinc-400 hover:text-red-400 hover:bg-red-500/10">
                      <TrashIcon size={14} />
                    </button>
                  </td>
                </tr>
                {isOpen && (
                  <tr data-testid={`bal-batches-${i}`}>
                    <td colSpan={11} className="px-4 py-3 bg-[var(--bg-0)]/60">
                      <div className="border border-[var(--border)] rounded-sm">
                        <div className="flex items-center justify-between px-3 py-2 border-b border-[var(--border)]">
                          <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-2">
                            <StackIcon size={12} weight="bold" /> Batch-Wise Stock · {r.rm_code}
                          </div>
                          <div className="text-[10px] font-mono text-zinc-500">
                            {bLoading ? "Loading…" : (batches ? `${batches.length} batch${batches.length === 1 ? "" : "es"}` : "")}
                          </div>
                        </div>
                        {bLoading ? (
                          <div className="text-center text-[11px] text-zinc-500 py-6">Fetching batch breakdown…</div>
                        ) : (batches && batches.length === 0) ? (
                          <div data-testid={`bal-batches-empty-${i}`} className="text-center text-[11px] text-amber-300/80 py-6 font-mono">
                            Aggregate stock at this stage — no batch-level movements recorded yet.
                          </div>
                        ) : (
                          <table className="w-full text-[11px]">
                            <thead className="bg-[var(--bg-2)]/30 text-[9px] uppercase tracking-widest text-zinc-500">
                              <tr>
                                <th className="text-left px-3 py-2">Batch #</th>
                                <th className="text-left px-3 py-2">Received Date</th>
                                <th className="text-left px-3 py-2">QC</th>
                                <th className="text-right px-3 py-2">Received</th>
                                <th className="text-right px-3 py-2">Consumed</th>
                                <th className="text-right px-3 py-2">Produced</th>
                                <th className="text-right px-3 py-2">Issued</th>
                                <th className="text-right px-3 py-2">Balance</th>
                                <th className="text-left px-3 py-2">UoM</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(batches || []).map((b, j) => (
                                <tr key={j} className="border-t border-[var(--border)]/40" data-testid={`bal-batch-${i}-${j}`}>
                                  <td className={`px-3 py-1.5 font-mono ${b.batch_no === "— Unbatched —" ? "text-zinc-500 italic" : "text-[var(--accent)]"}`}>{b.batch_no}</td>
                                  <td className="px-3 py-1.5 text-zinc-400 font-mono">{b.received_date || "—"}</td>
                                  <td className="px-3 py-1.5">
                                    {b.quality_status ? (
                                      <span className={`text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm border ${b.quality_status === "Passed" ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10" : b.quality_status === "Rejected" ? "border-red-500/40 text-red-300 bg-red-500/10" : "border-amber-500/40 text-amber-300 bg-amber-500/10"}`}>
                                        {b.quality_status}
                                      </span>
                                    ) : <span className="text-zinc-600">—</span>}
                                  </td>
                                  <td className="px-3 py-1.5 text-right font-mono tabular-nums text-emerald-300">{fmt(b.received)}</td>
                                  <td className="px-3 py-1.5 text-right font-mono tabular-nums text-amber-300">{fmt(b.consumed)}</td>
                                  <td className="px-3 py-1.5 text-right font-mono tabular-nums text-sky-300">{fmt(b.produced)}</td>
                                  <td className="px-3 py-1.5 text-right font-mono tabular-nums text-red-300">{fmt(b.issued)}</td>
                                  <td className={`px-3 py-1.5 text-right font-mono tabular-nums font-semibold ${Number(b.balance) < 0 ? "text-red-400" : Number(b.balance) === 0 ? "text-zinc-500" : "text-white"}`}>{fmt(b.balance)}</td>
                                  <td className="px-3 py-1.5 text-zinc-500 font-mono">{b.uom}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
                </>
              );
            })}
            {filtered.length === 0 && !loading && (
              <tr><td colSpan={11} className="text-center py-16 text-zinc-500 text-xs">No WIP-floor balances recorded yet</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
